import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
    BatchAction,
    DashboardWrapper,
    DashboardTitle,
    SearchDebounce,
    CactusModal,
    ModalDeleteConfirm,
    FormWidgetTime,

} from '../../components';
import useBatchAction from '../../hooks/useBatchAction';

import { useDispatch, useSelector } from 'react-redux';

import * as actionCreator from "../../actions";
import InfiniteScroll from 'react-infinite-scroller';
import { IconLoader } from '../../assets/icons/IconLoader';

import Widget, { isWidgetTime, WidgetSelection, getAllWidgetSelection } from '../../models/Widget';
import WidgetItem from '../../components/WidgetItem';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { ReactComponent as WidgetsIcon } from '../../assets/icons-menu/widgets.svg';
import ScreenItemSkeleton from '../../components/ScreenItem/ScreenItemSkeleton';

interface WidgetsProps {};

type IconSize = '1x' | '2x';
const ICON_SIZES: Record<IconSize, string> = {
    '1x': '24px',
    '2x': '32px'
};

const Widgets: React.FC<WidgetsProps> = (props: WidgetsProps) => {

    const [t] = useTranslation();

    const [search, setSearch] = useState<string>('');
    const batchAction = useBatchAction();

    const [selectedWidget, setSelectedWidget] = useState<Widget | null>(null);
    const [widgetTypeSelected, setWidgetTypeSelected] = useState<WidgetSelection | null>(null);

    const [creatingWidget, setCreatingWidget] = useState(false);
    const [editingWidget, setEditingWidget] = useState(false)
    const [deletingWidget, setDeletingWidget] = useState(false)

    const dispatch = useDispatch();

    const widgetReducer = useSelector( (store: any) => store.widgetReducer );
    const widgetPagination = widgetReducer.data;
    const widgets = widgetReducer.items;
    const isLoading = widgetReducer.isLoading;

    useEffect(() => {
        _fetchWidgets(1);
        setWidgetTypeSelected(null);
        setSelectedWidget(null);
    }, []);

    const refreshWidgets = () => {
        _fetchWidgets(1);
        setWidgetTypeSelected(null);
        setSelectedWidget(null);
    }

    const _fetchWidgets = (page?: number) => {
        dispatch( actionCreator.widgetCancelRequest() );
        dispatch( actionCreator.widgetFetch(page, search) );
    }

    const _handleCreateWidget = () => {
        setCreatingWidget(true)
    }

    const _handleEditWidget = (widget: Widget) => {
        setSelectedWidget(widget)
        setEditingWidget(true)
    }

    const _handleDeleteWidget = (widget: Widget) => {
        setSelectedWidget(widget)
        setDeletingWidget(true)
    }

    const _deleteWidget = async() => {
        
        if(!selectedWidget) return;

        try {
            dispatch(actionCreator.widgetDelete([selectedWidget]));
            setDeletingWidget(false)
            
        } catch (error) {
            setDeletingWidget(false)
        }
    }

    const _renderBatchAction = () => {

        if(!widgets || widgets.length === 0) return null;

        const batchActionView = batchAction.isSelected() ? (
            <>
                {t('Batch Action General Delete Message').split('\n').map((line: string, key: number) => (
                    <p key={key} className="small">{line}</p>
                ))}
                <button
                    className="btn btn-sm btn-danger mt-2"
                    style={{ fontSize: '0.75rem', padding: '3px 12px' }}
                    onClick={() => {
                        const selected: Widget[] = batchAction.getModelSelected();
                        dispatch( actionCreator.widgetDelete(selected, () => {
                            batchAction.reset();
                        }));
                    }}
                >
                    {t('Delete')}
                </button>
            </>
        ) : (
            <p className="small m-0">{t('Batch Action Suggestion')}</p>
        );

        return (
            <BatchAction
                isDisabled={isLoading}
                isActive={batchAction.isActive}
                onClickToggleAction={() => {
                    batchAction.reset();
                    batchAction.setIsActive(!batchAction.isActive);
                }}
                selectAll={batchAction.isSelectAll(widgets ?? [])}
                onClickSelectAll={(all: boolean) => {
                    if(all) batchAction.selectAll(widgets ?? []);
                    else batchAction.deselectAll();
                }}
                selectedCount={batchAction.getModelSelected().length}
            >
                {batchActionView}
            </BatchAction>
        );
    }

    const _renderSkeletonLoading = () => {
        return Array.from({length: 15}, (_, i) => i + 1).map((item: any, id: number) => {
            return (
                <div key={id} className="mb-3">
                    <ScreenItemSkeleton />
                </div>
            );
        });
    }

    const _renderWidgets = () => {
    
        //if(widgetReducer.isLoading) return _renderPlaylistMediaItemSkeleton();

        if(isLoading) {
            return _renderSkeletonLoading();
        }

        let allWidgets = widgets.map( (widget: Widget, index: number) => {
            /*return(
                <PlaylistResourceItem 
                    key={index} 
                    resource={widget} 
                    handleAddResource={_handleAddResourceToPlaylist}
                    handleDragMediaItems={_handleDragMediaItems}
                />
            )*/
            return(
                <WidgetItem
                    key={index}
                    widget={widget}
                    openModal={_handleEditWidget}
                    openDeleteModal={_handleDeleteWidget}
                    visibleCheck={batchAction.isActive}
                    isChecked={batchAction.isModelSelected(widget)}
                    onChecked={(w: Widget) => batchAction.changeModelValue(w)}
                />
            )
        });

        if(allWidgets.length === 0) {
            return(
                <div>
                    <div className="mb-4">
                        {t('No Result')}
                    </div>
                </div>
            )
        }

        return(
            <InfiniteScroll
                pageStart={widgetPagination?.from}
                loadMore={() => {
                    _fetchWidgets(widgetPagination?.current_page ? widgetPagination?.current_page + 1 : 1);
                }}
                hasMore={widgetPagination?.next_page_url !== null}
                loader={
                    <div key="loading" className="d-flex justify-content-center">
                        <IconLoader class='stroke-gray-light' styles={{ width: '50px', height: '50px' }} />
                    </div>
                }
            >
                {allWidgets}
            </InfiniteScroll>
        );

    }

    const _renderEditWidgetForm = () => {

        if(!selectedWidget) return;

        if(isWidgetTime(selectedWidget.mime_type)) {
            return(
                <div className="mt-3 col-12">
                    <FormWidgetTime
                        widgetSelected={widgetTypeSelected}
                        disableSubmitButton={isLoading}
                        widget={selectedWidget}
                        createWidget={(widget: Widget) => {
                            dispatch( 
                                actionCreator.widgetUpdate(widget, () => {
                                    setEditingWidget(false);
                                    setWidgetTypeSelected(null);
                                    setSelectedWidget(null);
                                })
                            );
                        }}
                    />
                </div>
            )
            
        }
    }

    const _renderBody = () => {
    
        let navigation;
        let content;

        if(widgetTypeSelected) {
            navigation = (
                <div className="col-12 pointer mb-3" onClick={() => setWidgetTypeSelected(null)}>
                    <FontAwesomeIcon icon={faArrowLeft} size="1x" />
                </div>
            );
            content = _renderWidgetSelected();
        }
        else {
            content = _renderWidgetNotSelected();
        }

        return(
            <>
                {navigation}
                <div className="col-12">
                    {content}
                </div>
            </>
        );

    }

    const _renderWidgetSelected = () => {
    
        let icon;

        if(widgetTypeSelected) icon = _getIcon(widgetTypeSelected, "1x");

        return(
            <>
                <div className="d-flex align-items-center">
                    <h6 className="m-0 mr-3">{ t('Create Widget') }</h6>
                    {icon}
                </div>
                <div className="mt-3">
                    <FormWidgetTime
                        widgetSelected={widgetTypeSelected}
                        disableSubmitButton={isLoading}
                        createWidget={(widget: Widget) => {
                            dispatch( 
                                actionCreator.widgetCreate(widget, () => {
                                    setCreatingWidget(false);
                                    setWidgetTypeSelected(null);
                                })
                            );
                        }}
                    />
                </div>
            </>
        );
    }

    const _renderWidgetNotSelected = () => {
        return(
            <>
                <h6 className="m-0">{ t('Select_widget_type') }</h6>
                <div className="mt-3">
                    {_renderWidgetsTypes()}
                </div>
            </>
        );
    }

    const _getIcon = (_item: WidgetSelection, size: IconSize = '1x') => {
        const dimension = ICON_SIZES[size] || ICON_SIZES['1x'];
        return <WidgetsIcon style={{ width: dimension, height: dimension }} />;
    }

    const _renderWidgetsTypes = () => {
    
        const widgets = getAllWidgetSelection().map((item: WidgetSelection, i: number) => {

            let icon = _getIcon(item, "2x");
            let text: string = t('filter_button_widget/time');

            return(
                <div 
                    key={i}
                    className="d-flex justify-content-center align-items-center flex-column bg-gray-dark pointer"
                    style={{ width: '100px', height: '100px' }}
                    onClick={() => setWidgetTypeSelected(item)}
                >
                    {icon}
                    <span className="mt-1" style={{ fontSize: '.9rem' }}>{text}</span>
                </div>
            );
        });

        return(
            <div className="d-flex">
                {widgets}
            </div>
        );

    }

    return(
        <DashboardWrapper>
            <div className="row align-items-center mb-1">
                <div className="col-12">
                    <DashboardTitle
                        title={t('Widgets')}
                        isLoading={isLoading}
                        withRefreshIcon={!isLoading}
                        refreshIconCallBack={() => {
                            refreshWidgets();
                        }}
                    />
                </div>
            </div>
            <div className="row align-items-center mb-3">
                <div className="col">
                    <SearchDebounce
                        placeholder={`${t('Name')}`}
                        borderColor="transparent"
                        onChange={(text: string) => setSearch(text)}
                        debounceFinish={(text: string) => { _fetchWidgets(1); batchAction.reset(); }}
                    />
                </div>
                <div className="col-auto">
                    <button
                        className="btn btn-success"
                        disabled={isLoading}
                        onClick={() => _handleCreateWidget()}
                    >
                        { t('New_widget') }
                    </button>
                </div>
            </div>

            <div className="mb-3">
                {_renderBatchAction()}
            </div>

            <div className="mt-3">
                {_renderWidgets()}
            </div>

            <CactusModal 
                show={creatingWidget}
                title={t('Create Widget')}
                onClose={() => {
                    setCreatingWidget(false)
                    setWidgetTypeSelected(null);
                }}
                minWidth="500px"
            >
                <div className='row'>
                    {_renderBody()}
                </div>
            </CactusModal>

            <CactusModal 
                show={editingWidget}
                title={t('Edit_widget')}
                onClose={() => {
                    setEditingWidget(false)
                }}
                minWidth="500px"
            >
                <div className='row'>
                    {_renderEditWidgetForm()}
                </div>
            </CactusModal>

            <ModalDeleteConfirm 
                show={deletingWidget}
                title={t('Delete_widget')}
                onCloseFunction={setDeletingWidget}
                onCloseFunctionParameter={false}
                overrideEntityTranslationAbove={ t('Are_you_sure_to_delete_current_m_') }
                entityTranslation={t('widget')}
                buttonLoading={false}
                onClickFunctionButton={ _deleteWidget }
                buttonText={t('Delete')}
            />

            
        </DashboardWrapper>
    );

};

export default React.memo(Widgets);
